
import { Question, QuestionContent, isQuestionContent } from "@/types/test";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Trash2 } from "lucide-react";

interface MultipleChoiceEditorProps {
  content: Question["content"];
  onUpdate: (content: Question["content"]) => void;
}

export const MultipleChoiceEditor = ({ content, onUpdate }: MultipleChoiceEditorProps) => {
  const questionContent = isQuestionContent(content) ? content : { question: '', options: [''] };

  const addOption = () => {
    onUpdate({
      ...questionContent,
      options: [...(questionContent.options || []), ""],
    });
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...(questionContent.options || [])];
    newOptions[index] = value;
    onUpdate({
      ...questionContent,
      options: newOptions,
    });
  };

  const removeOption = (index: number) => {
    const newOptions = [...(questionContent.options || [])];
    newOptions.splice(index, 1);
    onUpdate({
      ...questionContent,
      options: newOptions,
      correctOption: questionContent.correctOption === index 
        ? 0 
        : questionContent.correctOption && questionContent.correctOption > index 
          ? questionContent.correctOption - 1 
          : questionContent.correctOption,
    });
  };

  const setCorrectOption = (index: number) => {
    onUpdate({
      ...questionContent,
      correctOption: index,
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <Label>Options (Select the correct answer)</Label>
        <RadioGroup
          value={questionContent.correctOption?.toString()}
          onValueChange={(value) => setCorrectOption(parseInt(value))}
        >
          {questionContent.options?.map((option, index) => (
            <div key={index} className="flex items-center gap-2">
              <RadioGroupItem value={index.toString()} id={`option-${index}`} />
              <Input
                value={option}
                onChange={(e) => updateOption(index, e.target.value)}
                placeholder={`Option ${index + 1}`}
                className="bg-transparent"
              />
              {questionContent.options!.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeOption(index)}
                  className="text-[#8E9196] hover:text-[#ea384c]"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </RadioGroup>
      </div>
      <Button onClick={addOption} variant="outline" size="sm">
        Add Option
      </Button>
    </div>
  );
};
